import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { DEVICE } from '@core/misc/misc.utils';
import { environment } from '@env/environment';

/**
 * Zoom stays allowed: no `maximum-scale` / `minimum-scale` / `user-scalable=no` (WCAG 1.4.4, axe `meta-viewport`, website-web#79).
 * Side effect on iOS Safari: a focused input or select whose font-size is below 16px zooms the page in.
 */
const VIEWPORT_META_CONTENT = 'width=device-width, initial-scale=1.0, viewport-fit=cover';
const MOBILE_WEB_APP_TITLE = 'Kazunion';
const SCROLLABLE_INPUT_TAGS = ['INPUT', 'TEXTAREA'];
const FOCUS_SCROLL_DELAY_MS = 100;
const THEME_COLOR_CSS_VAR = '--theme-color';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  readonly #document = inject<Document>(DOCUMENT);
  readonly #meta = inject(Meta);

  /** Registers viewport / mobile meta tags and injects the theme stylesheet. */
  public init(): void {
    this.#addViewportTag();
    if (DEVICE.any()) this.#configureMobileDevice();
    this.#loadThemeStylesheet();
  }

  /** `updateTag` replaces the viewport meta of index.html, so the page keeps exactly one (`addTag` added a second one). */
  #addViewportTag(): void {
    this.#meta.updateTag({ name: 'viewport', content: VIEWPORT_META_CONTENT });
  }

  #configureMobileDevice(): void {
    this.#meta.addTag({ name: 'orientation', content: 'portrait' });
    this.#meta.addTag({ name: 'mobile-web-app-capable', content: 'yes' });
    this.#meta.addTag({ name: 'mobile-web-app-title', content: MOBILE_WEB_APP_TITLE });
    if (DEVICE.Android()) this.#inputFocusScrollFix();
    this.#preventDefaultLabelTouchFocus();
  }

  #loadThemeStylesheet(): void {
    const themeLink = this.#document.createElement('link');
    themeLink.type = 'text/css';
    themeLink.rel = 'stylesheet';
    themeLink.href = `${environment.serviceCode}.css?v${environment.buildVersion}`;
    themeLink.onload = (): void => this.#applyThemeColorMeta();
    this.#document.head.appendChild(themeLink);
  }

  #applyThemeColorMeta(): void {
    const themeColor = getComputedStyle(this.#document.documentElement).getPropertyValue(THEME_COLOR_CSS_VAR);
    if (!themeColor) return;
    this.#meta.addTag({ name: 'theme-color', content: themeColor.replace(/\s/g, '') });
  }

  #inputFocusScrollFix(): void {
    this.#document.body.addEventListener('focusin', () => {
      const activeElement = this.#document.activeElement;
      if (!activeElement || !SCROLLABLE_INPUT_TAGS.includes(activeElement.tagName)) return;
      setTimeout(() => activeElement.scrollIntoView({ block: 'center', behavior: 'smooth' }), FOCUS_SCROLL_DELAY_MS);
    });
  }

  #preventDefaultLabelTouchFocus(): void {
    this.#document.addEventListener('click', (event) => this.#handleLabelClick(event), { passive: false });
  }

  #handleLabelClick(event: Event): void {
    const target = event.target as HTMLLabelElement;
    if (target.tagName !== 'LABEL') return;

    const forId = target.getAttribute('for');
    if (!forId) return;

    const associatedElement = this.#document.getElementById(forId);
    // Prevent default focus only when the label is associated with a `<select>`.
    if (associatedElement?.tagName !== 'SELECT') return;

    event.preventDefault();
    associatedElement.click();
  }
}
